/**
 * /market/:slug/catalogo — PLP (catálogo comercial puro).
 */
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketPlpPage } from "@/components/market/MarketPlpPage";
import { findCategoryByParam } from "@/lib/market-landing";
import { MarketPlpSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/catalogo")({
  pendingComponent: MarketPlpSkeleton,
  pendingMs: 0,
  validateSearch: (search: Record<string, unknown>): {
    categoria?: string;
    q?: string;
    marca?: string;
    coleccion?: string;
    promo?: string;
  } => {
    const out: {
      categoria?: string;
      q?: string;
      marca?: string;
      coleccion?: string;
      promo?: string;
    } = {};
    if (typeof search.categoria === "string") out.categoria = search.categoria;
    if (typeof search.q === "string") out.q = search.q;
    if (typeof search.marca === "string") out.marca = search.marca;
    if (typeof search.coleccion === "string") out.coleccion = search.coleccion;
    if (typeof search.promo === "string") out.promo = search.promo;
    return out;
  },
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: CatalogPlpRoute,
  head: ({ params, loaderData }) => {
    const manifestLink = { rel: "manifest", href: `/api/manifest/${params.slug}` };
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Catálogo — ${name}` },
        { name: "description", content: `Explorá el catálogo de ${name}` },
      ],
      links: [manifestLink, FONT_LINK],
    };
  },
});

function CatalogPlpRoute() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;
  if (!result.ok) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <p className="text-muted-foreground">Catálogo no disponible.</p>
      </div>
    );
  }

  const catParam = search.categoria;
  const category = catParam ? findCategoryByParam(result.data.categories, catParam) : null;

  return (
    <MarketPlpPage
      slug={slug}
      data={result.data}
      initialCategoriaId={category?.id ?? null}
      initialSearch={search.q ?? ""}
      initialPromoId={search.promo ?? null}
    />
  );
}
